import {
  ChangeEvent,
  InputHTMLAttributes,
  ReactNode,
  KeyboardEvent,
  FocusEvent as ReactFocusEvent,
  MouseEvent,
  useState,
  MouseEventHandler,
  useRef,
  useEffect,
  CSSProperties,
} from "react";
import { Icon } from "../Icon";
import { styles } from "../InputField";
import { StatusText } from "../StatusText";

export const getDecimalsCount = (num: number) => {
  if (Number.isInteger(num)) return 0; // Check if it's already a whole number
  return num.toString().split(".")[1].length;
};

type NumberInputEvent = Omit<ChangeEvent<HTMLInputElement>, "target"> & {
  target: { value: number };
};

export interface StrictNumberInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  label?: string | ReactNode;
  hideLabel?: boolean;
  value?: number;
  className?: string;
  inputStyles?: CSSProperties;
  min?: number;
  max?: number;
  /**
   * Whether to allow an undefined value in the input (making it clearable)
   * @default false
   */
  allowUndefined?: boolean;
  /**
   * A number specifying the steps of incrementing or decrementing
   * Note: Will cause the input value to snap to the nearest step
   * @Default 1
   */
  step?: number;
  /** The number of decimals to allow for the input */
  allowDecimals?: number;
  icon?: string | ReactNode;
  subIcon?: string;
  addIcon?: string;
  placeholder?: string;
  disabled?: boolean;
  focus?: boolean;
  autoFocus?: boolean;
  large?: boolean;
  danger?: boolean;
  success?: boolean;
  dark?: boolean;
  required?: boolean;
  compact?: boolean;
  inputBgColor?: string;
  append?: ReactNode;
  appendEnd?: ReactNode;
  onChange?: (e: NumberInputEvent) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onKeyUp?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (e: ReactFocusEvent<HTMLInputElement, Element>) => void;
  onFocus?: (e: ReactFocusEvent<HTMLInputElement, Element>) => void;
  onButtonClick?: (
    e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>,
  ) => void;
  dangerText?: string;
  successText?: string;
  infoText?: string;
}

export const StrictNumberInput = ({
  id,
  label,
  hideLabel,
  value,
  className,
  inputStyles,
  min,
  max,
  allowUndefined = false,
  step = 1,
  allowDecimals,
  icon,
  subIcon,
  addIcon,
  placeholder,
  disabled,
  focus,
  autoFocus,
  large,
  danger,
  success,
  dark,
  required,
  compact,
  inputBgColor,
  append,
  appendEnd,
  onChange,
  onKeyDown,
  onKeyUp,
  onBlur,
  onFocus,
  onButtonClick,
  dangerText,
  successText,
  infoText,
  ...rest
}: StrictNumberInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [internalValue, setInternalValue] = useState(
    allowUndefined && (value === undefined || value === null)
      ? ""
      : (value?.toString() ?? "0"),
  );
  const internalValueNumber = Number(internalValue);
  const computedAllowedDecimals =
    allowDecimals !== undefined ? allowDecimals : getDecimalsCount(step); // if allowDecimals prop is not provided, fallback to allowing same number of decimals as in step prop. E.g. step = 0.5 -> computedAllowedDecimals = 1

  useEffect(() => {
    if (allowUndefined && (value === undefined || value === null)) {
      setInternalValue("");
    } else {
      setInternalValue((value ?? 0).toString());
    }
  }, [value, allowUndefined]);

  const canDecrement = () => {
    if (disabled) return false;
    if (allowUndefined && internalValue.trim() === "") return true;
    if (min === undefined) return true;
    return internalValueNumber > min;
  };

  const canIncrement = () => {
    if (disabled) return false;
    if (allowUndefined && internalValue.trim() === "") return true;
    if (max === undefined) return true;
    return internalValueNumber < max;
  };

  const handleDecrement: MouseEventHandler<HTMLButtonElement> = (e) => {
    onButtonClick?.(e);
    if (!canDecrement()) return;
    const newVal =
      allowUndefined && internalValue.trim() === ""
        ? (min ?? 0)
        : internalValueNumber - step;
    const factor = Math.pow(10, computedAllowedDecimals);
    const roundedVal = Math.round(newVal * factor) / factor;
    setInternalValue(roundedVal.toString());
    if (inputRef.current) {
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        HTMLInputElement.prototype,
        "value",
      )?.set;
      nativeInputValueSetter?.call(inputRef.current, roundedVal.toString());
      const event = new Event("input", { bubbles: true });
      inputRef.current.dispatchEvent(event);
      if (onChange) {
        onChange({
          target: { value: roundedVal },
        } as NumberInputEvent);
      }
      inputRef.current.focus();
    }
  };

  const handleIncrement: MouseEventHandler<HTMLButtonElement> = (e) => {
    onButtonClick?.(e);
    if (!canIncrement()) return;
    const newVal =
      allowUndefined && internalValue.trim() === ""
        ? step
        : internalValueNumber + step;
    const factor = Math.pow(10, computedAllowedDecimals);
    const roundedVal = Math.round(newVal * factor) / factor;
    setInternalValue(roundedVal.toString());
    if (inputRef.current) {
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        HTMLInputElement.prototype,
        "value",
      )?.set;
      nativeInputValueSetter?.call(inputRef.current, roundedVal.toString());
      const event = new Event("input", { bubbles: true });
      inputRef.current.dispatchEvent(event);
      if (onChange) {
        onChange({
          target: { value: roundedVal },
        } as NumberInputEvent);
      }
      inputRef.current.focus();
    }
  };

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setInternalValue(raw);
    let val = parseFloat(raw);

    if (onChange) {
      if (allowUndefined && raw.trim() === "") {
        onChange({
          ...e,
          target: { ...e.target, value: undefined as unknown as number },
        });
        return;
      }

      if (isNaN(val)) val = 0;

      if (min !== undefined && val < min) val = min;
      if (max !== undefined && val > max) val = max;

      const factor = Math.pow(10, computedAllowedDecimals);
      val = Math.round(val * factor) / factor;

      onChange({ ...e, target: { ...e.target, value: val } });
      setInternalValue(val.toString());
    }
  };

  const handleOnKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (onKeyDown) onKeyDown(e);
  };

  const handleOnKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    if (onKeyUp) onKeyUp(e);
  };

  const handleOnBlur = (e: ReactFocusEvent<HTMLInputElement, Element>) => {
    if (onBlur) onBlur(e);
    handleOnChange(e);
  };

  const handleOnFocus = (e: ReactFocusEvent<HTMLInputElement, Element>) => {
    if (onFocus) onFocus(e);
  };

  return (
    <div
      className={`
    ${styles.inputContainer} 
    ${dark ? styles.dark : ""}`}
      {...rest}
      data-invalid={danger || dangerText ? "true" : undefined}
    >
      {!!label && (
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "end",
            justifyContent: "flex-start",
          }}
        >
          <label
            htmlFor={`${label}_INPUT`}
            className={hideLabel || (compact && hideLabel) ? "sr-only" : ""}
          >
            {label}
            {required && typeof label === "string" && (
              <>
                {" "}
                <strong aria-hidden>*</strong>
                <span className="sr-only">(required)</span>
              </>
            )}
          </label>
        </div>
      )}
      <div
        className={`
        ${className ? className : ""} 
        ${styles.inputField} 
        ${focus ? styles.focus : ""}
        ${large ? styles.large : ""}
        ${danger || (dangerText && dangerText.length > 0) ? styles.danger : ""}
        ${
          success || (successText && successText.length > 0)
            ? styles.success
            : ""
        }
        ${dark ? styles.dark : ""}
        ${disabled ? styles.disabled : ""}`}
        {...(inputBgColor ? { style: { backgroundColor: inputBgColor } } : {})}
      >
        <button
          type="button"
          className={`${styles.incrementDecrementBtn} decrement-btn--${id ?? "number-input"}`}
          disabled={!canDecrement()}
          onClick={handleDecrement}
          style={{ marginLeft: -10 }}
        >
          <Icon name={subIcon ?? "subtract"} />
          <span className="sr-only">Decrement</span>
        </button>
        {icon &&
          (typeof icon === "string" ? (
            <Icon className={styles.icon} name={icon} />
          ) : (
            icon
          ))}
        <input
          ref={inputRef}
          id={id || `${label}_INPUT`}
          autoFocus={autoFocus}
          className={styles.input}
          type="number"
          placeholder={placeholder}
          disabled={disabled}
          min={min}
          max={max}
          required={required}
          step={computedAllowedDecimals > 0 ? "any" : step}
          value={internalValue}
          onChange={(e) => setInternalValue(e.target.value)}
          onKeyDown={handleOnKeyDown}
          onKeyUp={handleOnKeyUp}
          onBlur={handleOnBlur}
          onFocus={handleOnFocus}
          style={{ marginRight: 0, ...inputStyles }}
        />
        {append && append}
        <button
          type="button"
          className={`${styles.incrementDecrementBtn} increment-btn--${id ?? "number-input"}`}
          disabled={!canIncrement()}
          onClick={handleIncrement}
          style={{ marginRight: 10 }}
        >
          <Icon name={addIcon ?? "add"} />
          <span className="sr-only">Increment</span>
        </button>
        {appendEnd && appendEnd}
      </div>
      <div className={styles.bottomContainer}>
        {!dangerText && !successText && !infoText && !compact ? (
          <div style={{ height: "24px" }}></div>
        ) : dangerText ? (
          <StatusText text={dangerText} fail dark={dark} />
        ) : successText ? (
          <StatusText text={successText} success dark={dark} />
        ) : (
          !!infoText && <StatusText text={infoText} dark={dark} />
        )}
      </div>
    </div>
  );
};
